import React, { useState, useEffect } from 'react';
import {
  Box,
  Heading,
  VStack,
  Card,
  CardHeader,
  CardBody,
  Button,
  Input,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Select,
  useColorModeValue,
  Text,
  IconButton,
  Tooltip,
  HStack,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  InputGroup,
  InputLeftElement,
  Icon,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { FiCopy, FiTrash2, FiEdit2, FiPlus, FiMoreVertical, FiSearch } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';

const MotionCard = motion(Card);

// TEMPORARY: Reference Icon to help with potential import issues
const _tempIconCheck = Icon;

// Dummy clubs data
const dummyClubs = [
  { id: '1', name: 'Tech Innovators user' },
  { id: '2', name: 'Creative Writers Guild' },
  { id: '3', name: 'Sports Analytics user' },
  { id: '4', name: 'Science Explorers' },
  { id: '5', name: 'Art & Design Society' },
];

// Dummy API keys data
const initialApiKeys = [
  {
    id: 1,
    name: 'Production API Key',
    key: 'pk_live_123456789abcdef',
    status: 'active',
    createdAt: '2024-03-15',
    lastUsed: '2024-03-20',
    usage: 1500,
    type: 'generic',
    clubId: '1',
    clubName: 'Tech Innovators user',
  },
  {
    id: 2,
    name: 'Development API Key',
    key: 'pk_test_987654321fedcba',
    status: 'active',
    createdAt: '2024-03-10',
    lastUsed: '2024-03-19',
    usage: 750,
    type: 'generic',
    clubId: '2',
    clubName: 'Creative Writers Guild',
  },
  {
    id: 3,
    name: 'Test API Key',
    key: 'pk_test_abcdef123456789',
    status: 'inactive',
    createdAt: '2024-03-05',
    lastUsed: '2024-03-18',
    usage: 300,
    type: 'generic',
    clubId: '3',
    clubName: 'Sports Analytics user',
  },
  {
    id: 4,
    name: 'Retell AI Integration',
    key: 're_live_abcdef123456789',
    status: 'active',
    createdAt: '2024-04-01',
    lastUsed: '2024-04-25',
    usage: 5000,
    type: 'retell',
    agentProductId: 'ag_12345',
    clubId: '1',
    clubName: 'Tech Innovators user',
  },
  {
    id: 5,
    name: 'Voiceflow Integration',
    key: 'vf_live_fedcba987654321',
    status: 'active',
    createdAt: '2024-04-05',
    lastUsed: '2024-04-26',
    usage: 7000,
    type: 'voiceflow',
    agentProductId: 'vf_agent_67890',
    clubId: '2',
    clubName: 'Creative Writers Guild',
  },
];

const ApiKeys = () => {
  const [clubs, setClubs] = useState([]); // State for clubs
  const [apiKeys, setApiKeys] = useState(initialApiKeys);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingKey, setEditingKey] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    status: 'active',
    type: 'generic',
    clubId: '',
    retell: {
      apiKey: '',
      agentId: '',
      knowledgeBaseId: '',
    },
    voiceflow: {
      apiKey: '',
      agentId: '',
      knowledgeBaseId: '',
    }
  });
  const toast = useToast();
  const { isSuperAdmin } = useAuth();
  const bgGradient = useColorModeValue('linear(to-br, blue.50, purple.100)', 'linear(to-br, gray.800, purple.900)');
  const cardBg = useColorModeValue('white', 'gray.700');
  const [searchQuery, setSearchQuery] = useState(''); // State for search query

  useEffect(() => {
    // Simulate fetching clubs
    setClubs(dummyClubs);
  }, []);

  const handleOpenModal = (key = null) => {
    if (key) {
      setEditingKey(key);
      setFormData({
        name: key.name || '',
        status: key.status || 'active',
        type: key.type || 'generic',
        clubId: key.clubId || '',
        retell: {
          apiKey: key.retellCredentials?.apiKey || '',
          agentId: key.retellCredentials?.agentId || '',
          knowledgeBaseId: key.retellCredentials?.knowledgeBaseId || '',
        },
        voiceflow: {
          apiKey: key.voiceflowCredentials?.apiKey || '',
          agentId: key.voiceflowCredentials?.agentId || '',
          knowledgeBaseId: key.voiceflowCredentials?.knowledgeBaseId || '',
        }
      });
    } else {
      setEditingKey(null);
      setFormData({
        name: '',
        status: 'active',
        type: 'generic',
        clubId: '',
        retell: {
          apiKey: '',
          agentId: '',
          knowledgeBaseId: '',
        },
        voiceflow: {
          apiKey: '',
          agentId: '',
          knowledgeBaseId: '',
        }
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingKey(null);
    setFormData({
      name: '',
      status: 'active',
      type: 'generic',
      clubId: '',
      retell: {
        apiKey: '',
        agentId: '',
        knowledgeBaseId: '',
      },
      voiceflow: {
        apiKey: '',
        agentId: '',
        knowledgeBaseId: '',
      }
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const selectedClub = clubs.find(club => club.id === formData.clubId);
    
    const newKeyData = {
      id: editingKey ? editingKey.id : apiKeys.length + 1,
      name: formData.name,
      status: formData.status,
      createdAt: editingKey ? editingKey.createdAt : new Date().toISOString().split('T')[0],
      lastUsed: editingKey ? editingKey.lastUsed : new Date().toISOString().split('T')[0],
      usage: editingKey ? editingKey.usage : 0,
      type: formData.type,
      clubId: formData.clubId,
      clubName: selectedClub ? selectedClub.name : 'Unknown user',
      retellCredentials: formData.retell,
      voiceflowCredentials: formData.voiceflow,
    };

    if (formData.retell.apiKey) {
      newKeyData.key = formData.retell.apiKey;
    } else if (formData.voiceflow.apiKey) {
      newKeyData.key = formData.voiceflow.apiKey;
    } else {
      newKeyData.key = editingKey && editingKey.type === 'generic' ? editingKey.key : `pk_${Math.random().toString(36).substring(2, 15)}`;
    }

    if (editingKey) {
      setApiKeys(apiKeys.map(key =>
        key.id === editingKey.id
          ? newKeyData
          : key
      ));
      toast({
        title: 'API Key Updated',
        status: 'success',
        duration: 3000,
      });
    } else {
      setApiKeys([...apiKeys, newKeyData]);
      toast({
        title: 'New API Key Created',
        status: 'success',
        duration: 3000,
      });
    }
    handleCloseModal();
  };

  const handleDelete = (id) => {
    if (id === 4 || id === 5) {
      toast({
        title: 'Deletion Restricted',
        description: 'This is a dummy key and cannot be deleted.',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    setApiKeys(apiKeys.filter(key => key.id !== id));
    toast({
      title: 'API Key Deleted',
      status: 'success',
      duration: 3000,
    });
  };

  const handleCopyKey = (keyToCopy) => {
    navigator.clipboard.writeText(keyToCopy);
    toast({
      title: 'API Key Copied',
      status: 'success',
      duration: 2000,
    });
  };

  const displayedApiKeys = (isSuperAdmin() ? apiKeys : apiKeys.filter(key => key.type === 'generic'))
    .filter(key =>
      key.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      key.clubName.toLowerCase().includes(searchQuery.toLowerCase())
    );

  return (
    <Box minH="100vh" bgGradient={bgGradient} p={4}>
      <Heading size="lg" mb={6} bgGradient="linear(to-r, purple.500, blue.400)" bgClip="text">
        API Keys Management
      </Heading>

      <MotionCard whileHover={{ scale: 1.02 }} bg={cardBg} mb={6}>
        <CardHeader>
          {isSuperAdmin() && (
            <Button
              leftIcon={<FiPlus />}
              colorScheme="purple"
              onClick={() => handleOpenModal()}
            >
              Create New API Key
            </Button>
          )}
        </CardHeader>
        <CardBody>
          <HStack spacing={4} mb={4}>
            <InputGroup width="300px">
              <InputLeftElement
                pointerEvents="none"
                children={<Icon as={FiSearch} color="gray.400" />}
              />
              <Input
                placeholder="Search by key name or user name"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                bg={useColorModeValue('gray.100', 'gray.700')}
                borderColor={useColorModeValue('gray.200', 'gray.600')}
                _hover={{ borderColor: useColorModeValue('blue.300', 'blue.600') }}
                _focus={{ borderColor: useColorModeValue('blue.500', 'blue.500'), boxShadow: 'outline' }}
                _placeholder={{ color: useColorModeValue('gray.400', 'gray.500') }}
              />
            </InputGroup>
          </HStack>
          <Box overflowX="auto">
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>Name</Th>
                  <Th>Type</Th>
                  <Th>API Key</Th>
                  <Th>user</Th>
                  <Th>Agent Product ID</Th>
                  <Th>Status</Th>
                  <Th>Created</Th>
                  <Th>Last Used</Th>
                  <Th>Usage</Th>
                  <Th>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {displayedApiKeys.map((key) => (
                  <Tr key={key.id}>
                    <Td>{key.name}</Td>
                    <Td>
                      <Badge colorScheme={key.type === 'retell' ? 'teal' : key.type === 'voiceflow' ? 'blue' : 'gray'}>
                        {key.type.charAt(0).toUpperCase() + key.type.slice(1)}
                      </Badge>
                    </Td>
                    <Td>
                      <HStack spacing={2}>
                        <Text fontFamily="mono" fontSize="sm">
                          {key.key ? `${key.key.substring(0, 8)}...` : 'N/A'}
                        </Text>
                        {key.key && (
                          <Tooltip label="Copy API Key" hasArrow>
                            <IconButton
                              aria-label="Copy API Key"
                              icon={<FiCopy />}
                              size="xs"
                              onClick={() => handleCopyKey(key.key)}
                              variant="ghost"
                            />
                          </Tooltip>
                        )}
                      </HStack>
                    </Td>
                    <Td>{key.clubName}</Td>
                    <Td>{key.agentProductId || 'N/A'}</Td>
                    <Td>
                      <Badge colorScheme={key.status === 'active' ? 'green' : 'red'}>
                        {key.status.charAt(0).toUpperCase() + key.status.slice(1)}
                      </Badge>
                    </Td>
                    <Td>{key.createdAt}</Td>
                    <Td>{key.lastUsed}</Td>
                    <Td>{key.usage}</Td>
                    <Td>
                      <Menu>
                        <MenuButton
                          as={IconButton}
                          icon={<FiMoreVertical />}
                          variant="ghost"
                          size="sm"
                        />
                        <MenuList>
                          {isSuperAdmin() && (
                            <MenuItem
                              icon={<FiEdit2 />}
                              onClick={() => handleOpenModal(key)}
                            >
                              Edit
                            </MenuItem>
                          )}
                          {isSuperAdmin() && (
                            <MenuItem
                              icon={<FiTrash2 />}
                              onClick={() => handleDelete(key.id)}
                              color="red.500"
                            >
                              Delete
                            </MenuItem>
                          )}
                        </MenuList>
                      </Menu>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        </CardBody>
      </MotionCard>

      <Modal isOpen={isModalOpen} onClose={handleCloseModal} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{editingKey ? 'Edit API Key' : 'Create New API Key'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <form onSubmit={handleSubmit}>
              <VStack spacing={4}>
                <FormControl isRequired>
                  <FormLabel>Name</FormLabel>
                  <Input
                    name="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Enter a name for the API key"
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Select user</FormLabel>
                  <Select
                    value={formData.clubId}
                    onChange={(e) => setFormData({ ...formData, clubId: e.target.value })}
                    placeholder="Select a user"
                  >
                    {clubs.map((club) => (
                      <option key={club.id} value={club.id}>
                        {club.name}
                      </option>
                    ))}
                  </Select>
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Type</FormLabel>
                  <Select
                    name="type"
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  >
                    <option value="generic">Generic API Key</option>
                    <option value="retell">Retell AI</option>
                    <option value="voiceflow">Voiceflow</option>
                  </Select>
                </FormControl>

                <Box w="full" borderWidth="1px" borderRadius="md" p={4}>
                  <Heading size="sm" mb={3}>Retell AI Credentials</Heading>
                  <VStack spacing={3}>
                    <FormControl isRequired={formData.type === 'retell'}>
                      <FormLabel>API Key</FormLabel>
                      <Input
                        value={formData.retell.apiKey}
                        onChange={(e) => setFormData({ ...formData, retell: { ...formData.retell, apiKey: e.target.value } })}
                        placeholder="Enter Retell API Key"
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Agent ID (Optional)</FormLabel>
                      <Input
                        value={formData.retell.agentId}
                        onChange={(e) => setFormData({ ...formData, retell: { ...formData.retell, agentId: e.target.value } })}
                        placeholder="Enter Retell Agent ID"
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Knowledge Base ID (Optional)</FormLabel>
                      <Input
                        value={formData.retell.knowledgeBaseId}
                        onChange={(e) => setFormData({ ...formData, retell: { ...formData.retell, knowledgeBaseId: e.target.value } })}
                        placeholder="Enter Retell Knowledge Base ID"
                      />
                    </FormControl>
                  </VStack>
                </Box>

                <Box w="full" borderWidth="1px" borderRadius="md" p={4}>
                  <Heading size="sm" mb={3}>Voiceflow Credentials</Heading>
                  <VStack spacing={3}>
                    <FormControl isRequired={formData.type === 'voiceflow'}>
                      <FormLabel>API Key</FormLabel>
                      <Input
                        value={formData.voiceflow.apiKey}
                        onChange={(e) => setFormData({ ...formData, voiceflow: { ...formData.voiceflow, apiKey: e.target.value } })}
                        placeholder="Enter Voiceflow API Key"
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Agent ID (Optional)</FormLabel>
                      <Input
                        value={formData.voiceflow.agentId}
                        onChange={(e) => setFormData({ ...formData, voiceflow: { ...formData.voiceflow, agentId: e.target.value } })}
                        placeholder="Enter Voiceflow Agent ID"
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Knowledge Base ID (Optional)</FormLabel>
                      <Input
                        value={formData.voiceflow.knowledgeBaseId}
                        onChange={(e) => setFormData({ ...formData, voiceflow: { ...formData.voiceflow, knowledgeBaseId: e.target.value } })}
                        placeholder="Enter Voiceflow Knowledge Base ID"
                      />
                    </FormControl>
                  </VStack>
                </Box>

                <FormControl isRequired>
                  <FormLabel>Status</FormLabel>
                  <Select
                    name="status"
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </Select>
                </FormControl>

                <Button type="submit" colorScheme="purple" width="full">
                  {editingKey ? 'Update API Key' : 'Create API Key'}
                </Button>
              </VStack>
            </form>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default ApiKeys; 